/**
 * Information Sharing Task — Study 2 for IJIM R&R.
 *
 * Flow per article:
 *   1. Show article as realistic news page replica (with mouse tracking)
 *   2. Participant clicks "Continue to Questions" (triggers FTT trajectory classification)
 *   3. Post-article survey: 20 Likert items (original + strengthening)
 *   4. Repeat for article 2
 *   5. Navigate to debrief/completion
 *
 * NO Share/Skip/Flag buttons — sharing is measured via IS1/IS2 Likert items.
 * This matches the original Study 1 design where sharing intention is a scale, not an action.
 *
 * Each participant sees 2 articles (1 true, 1 false) from the same topic pair.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExperiment } from '../../contexts/ExperimentContext';
import { useTracker } from '../../contexts/TrackerContext';
import PostArticleSurvey, { PostArticleSurveyData } from './PostArticleSurvey';
import MemoryTest, { MemoryTestData } from './MemoryTest';
import NewsArticleRenderer from '../news/NewsArticleRenderer';
import { getArticlesByCongruence, balanceArticles, NewsArticle } from '../../data/newsArticlesWithCongruence';
import { savePhaseData } from '../../services/api';

// Build a structured, analysis-ready response record matching the backend
// informationSharingTaskData.responses schema.
const buildResponseRecord = (article: any, surveyData: any, decision: string, fttResult: any, readingTime: number, responseTime: number) => ({
  articleId: article.id,
  decision,
  isFake: article.isFake,
  source: article.source,
  category: article.category,
  topic: article.topic,
  partisanDirection: article.partisanDirection,
  // Descriptive render metadata only — NOT a design IV (credibility is MEASURED via SEC).
  sourceTemplateStyle: article.credibilityLevel,
  // Endorser shown with the article (part of the Source-Endorser Credibility cue).
  endorser: article.endorser,
  // NOTE: no derived "congruence" — this study is the cue-typology design, not a
  // congruence design. Veracity (isFake) is the moderator; cues are measured.
  gistCues: article.gistCues,
  verbatimCues: article.verbatimCues,
  fttClassification: fttResult,
  postArticleSurvey: surveyData,
  readingTime,
  responseTime,
  presentationOrder: article.presentationOrder,
  timestamp: Date.now(),
});

// Persist Study 2 data via the durable phase endpoint (independent of the
// fire-and-forget tracking path) AND mirror to localStorage as a last-resort
// backup that survives a failed/blocked network until the debrief can flush it.
const persistInfoSharing = async (participantId: string, responsesMap: Map<string, any>, presentationSequence: any[] = []) => {
  const responses = Array.from(responsesMap.values());
  const payload = { startTime: undefined as any, endTime: Date.now(), responses, presentationSequence };
  try {
    localStorage.setItem(`infoSharingBackup_${participantId}`, JSON.stringify(payload));
  } catch (e) {
    console.error('Could not write infoSharing localStorage backup:', e);
  }
  await savePhaseData(participantId, 'info-sharing-task', payload);
};

// FTT classifier overrides for the info-sharing task. News reading takes far
// longer than a phishing decision, so the temporal sigmoid `center` (default 15s,
// tuned for phishing) is too small here and saturates the temporal sub-score.
// CONFIGURE LATER (no pilot needed — set from the live readingTime distribution or
// theory): e.g. `{ temporal: { center: 90000 } }`. Empty = use DEFAULT_FTT_CONFIG.
// Raw readingTime + trajectory metrics are stored per response, so anything can be
// re-classified offline once the final center is chosen.
const INFO_SHARING_FTT_CONFIG = {};

interface ArticleData extends NewsArticle {
  completed: boolean;
  presentationOrder?: number;
}

type Phase = 'reading' | 'survey' | 'behavioral' | 'memory' | 'transition';

const InformationSharingTask: React.FC = () => {
  const navigate = useNavigate();
  const { experimentData, updateFTTClassification } = useExperiment();
  const { tracker, trackMouseMovement, trackHover, classifyCurrentResponse } = useTracker();

  const [articles, setArticles] = useState<ArticleData[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('reading');
  const [articleStartTime, setArticleStartTime] = useState(Date.now());
  const [fttResult, setFttResult] = useState<any>(null);
  const [pendingSurveyData, setPendingSurveyData] = useState<PostArticleSurveyData | null>(null);
  const [behavioralStartTime, setBehavioralStartTime] = useState(0);

  const articleResponses = useRef(new Map<string, any>());
  // Explicit presentation sequence (Latin-square order) captured at load, for
  // clean order-effect analysis — independent of completion order.
  const presentationSequence = useRef<any[]>([]);
  // Memory-test plan: 2 of the 4 articles get a recognition test. The earlier one
  // (presentation order) is tested with INCIDENTAL encoding (surprise); the later
  // one is INTENTIONAL (participant now knows a test can come). See MemoryTest.
  const memoryPlan = useRef<{ ids: Set<string>; role: Record<string, 'incidental' | 'intentional'> }>({ ids: new Set(), role: {} });
  // Hover dwell-debounce: only record a region hover once the cursor has stayed
  // ≥250ms (filters quick pass-throughs that would otherwise spam enter/leave
  // events as the mouse crosses paragraph boundaries). Frontend-only throttle.
  const hoverRef = useRef<{ timer: any; active: string | null }>({ timer: null, active: null });
  // Re-entrancy guard for the share/skip/flag decision. The handler is async and
  // (on the last, non-memory-tested article) awaits a network save with the
  // buttons still visible — a double-click would re-run it after the survey has
  // been cleared, writing an EMPTY survey over the good one. This synchronous
  // ref blocks the 2nd+ invocation; `deciding` also disables the buttons for UX.
  const decidingRef = useRef(false);
  const [deciding, setDeciding] = useState(false);
  const participantId = experimentData?.participant?.id || '';

  // Load articles
  useEffect(() => {
    if (!participantId) return;

    let selected = getArticlesByCongruence(participantId);
    selected = balanceArticles(selected);

    // NOTE: resolveOrgTemplates (built for the phishing email/device study) is
    // intentionally NOT applied here — news articles are controlled stimuli and
    // must not be mutated per participant, which would break the verbatim-cue
    // manipulation.

    const withState: ArticleData[] = selected.map((a, idx) => ({
      ...a,
      completed: false,
      presentationOrder: idx + 1,
    }));

    // Pick 2 of the 4 positions to memory-test; earlier = incidental, later = intentional.
    const idxs = [0, 1, 2, 3].sort(() => Math.random() - 0.5).slice(0, 2).sort((a, b) => a - b);
    const plan = { ids: new Set<string>(), role: {} as Record<string, 'incidental' | 'intentional'> };
    if (withState[idxs[0]]) { plan.ids.add(withState[idxs[0]].id); plan.role[withState[idxs[0]].id] = 'incidental'; }
    if (withState[idxs[1]]) { plan.ids.add(withState[idxs[1]].id); plan.role[withState[idxs[1]].id] = 'intentional'; }
    memoryPlan.current = plan;

    presentationSequence.current = withState.map(a => ({
      articleId: a.id, category: a.category, isFake: a.isFake,
      topic: a.topic, source: a.source, presentationOrder: a.presentationOrder,
    }));

    setArticles(withState);
    setArticleStartTime(Date.now());

    tracker.track('taskStart', {
      taskType: 'info-sharing',
      articleCount: withState.length,
      articleIds: withState.map(a => a.id),
      topicPair: withState[0]?.topicPair,
      timestamp: Date.now(),
    });
  }, [participantId]);

  const currentArticle = articles[currentIndex];

  // ── Scroll depth + reading pauses (window-level) ────────────────────
  // The article scrolls at the window level, so we listen on window (the email
  // task's element-scroll handler does not apply here). Records max depth reached
  // and pauses (≥600ms still after scrolling) as a reading-depth signal.
  useEffect(() => {
    if (phase !== 'reading' || !currentArticle) return;
    let maxDepth = 0;
    let pauseTimer: any = null;
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      const depth = scrollable > 0 ? Math.min(100, Math.round((window.scrollY / scrollable) * 100)) : 100;
      if (depth > maxDepth) maxDepth = depth;
      if (pauseTimer) clearTimeout(pauseTimer);
      pauseTimer = setTimeout(() => {
        tracker.track('articleScrollPause', { articleId: currentArticle.id, depthPercent: depth, maxDepthPercent: maxDepth, timestamp: Date.now() });
      }, 600);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (pauseTimer) clearTimeout(pauseTimer);
      // Cancel any pending hover-dwell timer so it can't fire for the prior article.
      if (hoverRef.current.timer) { clearTimeout(hoverRef.current.timer); hoverRef.current.timer = null; }
      hoverRef.current.active = null;
      // Record the deepest scroll reached for this article on unmount/advance.
      tracker.track('articleScrollSummary', { articleId: currentArticle.id, maxDepthPercent: maxDepth, timestamp: Date.now() });
    };
  }, [phase, currentArticle, tracker]);

  // ── Text selection (verbatim-scrutiny signal) ──────────────────────
  // On mouseup, capture any selected text + the cue region it came from (via the
  // nearest [data-region] ancestor set by NewsArticleRenderer).
  useEffect(() => {
    if (phase !== 'reading' || !currentArticle) return;
    const onMouseUp = () => {
      const sel = window.getSelection();
      const text = sel ? sel.toString().trim() : '';
      if (!text) return;
      let region = 'unknown';
      const node = sel?.anchorNode;
      const el = (node && (node.nodeType === 1 ? node : node.parentElement)) as HTMLElement | null;
      const regionEl = el?.closest?.('[data-region]') as HTMLElement | null;
      if (regionEl) region = regionEl.getAttribute('data-region') || 'unknown';
      tracker.track('textSelection', {
        articleId: currentArticle.id,
        region,
        text: text.slice(0, 300), // cap payload
        length: text.length,
        timestamp: Date.now(),
      });
    };
    document.addEventListener('mouseup', onMouseUp);
    return () => document.removeEventListener('mouseup', onMouseUp);
  }, [phase, currentArticle, tracker]);

  // ── Participant finishes reading, clicks Continue ───────────────────

  const handleContinueToSurvey = () => {
    if (!currentArticle) return;

    const readingTime = Date.now() - articleStartTime;

    // FTT trajectory classification on the reading period.
    // CONTAMINATION FIX: the processing-mode label (gist/verbatim) is the Study 2
    // DEPENDENT variable, so it must NOT be informed by the article's truth
    // status, which is an INDEPENDENT variable. We therefore pass isPhishing:false
    // (the classifier's +0.2 truth term is suppressed for every article — see
    // fttClassifier.calculateContextualScore) so the label is derived from
    // behaviour/timing only. scenarioComplexity is left as a uniform constant
    // (identical across conditions → cannot confound the Congruence x Truth
    // comparison); deriving it from stimulus features would REINTRODUCE
    // confounding, so we deliberately do not.
    const fttResponseId = `infosharing-${currentArticle.id}-${Date.now()}`;
    const fttContext = {
      isPhishing: false, // do NOT feed ground truth into the DV
      scenarioComplexity: 0.5, // uniform constant, condition-independent
      priorResponses: articleResponses.current.size,
      cognitiveLoad: articleResponses.current.size / Math.max(articles.length, 1),
      // Classifier tuning overrides (empty for now → defaults). Configure the
      // temporal center here later; see INFO_SHARING_FTT_CONFIG.
      fttConfig: INFO_SHARING_FTT_CONFIG,
    };
    const classification = classifyCurrentResponse(fttResponseId, readingTime, fttContext);
    updateFTTClassification('info-sharing', fttResponseId, classification);
    setFttResult(classification);

    // Track reading completion
    tracker.track('articleReadComplete', {
      articleId: currentArticle.id,
      isFake: currentArticle.isFake,
      source: currentArticle.source,
      topic: currentArticle.topic,
      topicPair: currentArticle.topicPair,
      category: currentArticle.category,
      partisanDirection: currentArticle.partisanDirection,
      credibilityLevel: currentArticle.credibilityLevel,
      fttClassification: classification,
      readingTime,
      presentationOrder: currentArticle.presentationOrder,
      timestamp: Date.now(),
    });

    setPhase('survey');
  };

  // ── Post-article survey complete ────────────────────────────────────

  const handleSurveyComplete = (surveyData: PostArticleSurveyData) => {
    // Store survey data, move to behavioral decision
    setPendingSurveyData(surveyData);
    setBehavioralStartTime(Date.now());
    // Re-arm the decision guard for this article's behavioral screen.
    decidingRef.current = false;
    setDeciding(false);
    setPhase('behavioral');
  };

  // ── Behavioral share decision (AFTER survey, unanchored) ────────────

  const handleBehavioralDecision = async (decision: 'share' | 'skip' | 'flag') => {
    // Block double-clicks / re-entry: only the first click for this article may
    // build+save the record. Without this, a 2nd click after pendingSurveyData
    // was cleared would persist an empty survey over the real answers.
    if (decidingRef.current) return;
    // Never record a decision without the survey it belongs to (defensive: if
    // somehow entered without a survey, bail rather than save a null survey).
    if (!pendingSurveyData) { console.error('Behavioral decision with no pending survey — ignoring'); return; }
    decidingRef.current = true;
    setDeciding(true);

    const surveyData = pendingSurveyData;
    const readingTime = Date.now() - articleStartTime;
    const responseTime = behavioralStartTime ? Date.now() - behavioralStartTime : 0;

    // Full structured record (matches backend schema) — single source of truth
    // for both persistence paths and the localStorage backup.
    const record = buildResponseRecord(currentArticle, surveyData, decision, fttResult, readingTime, responseTime);
    articleResponses.current.set(currentArticle.id, record);

    // BACKUP FIRST: mirror to localStorage immediately, before any navigation or
    // network call, so a refresh/crash/blocked network can never lose this article.
    try {
      localStorage.setItem(
        `infoSharingBackup_${participantId}`,
        JSON.stringify({ endTime: Date.now(), responses: Array.from(articleResponses.current.values()) })
      );
    } catch (e) {
      console.error('Could not write infoSharing backup:', e);
    }

    // Path 1: tracking event (now includes responseTime, previously dropped)
    tracker.track('infoSharingDecision', {
      articleId: currentArticle.id,
      decision, // behavioral share decision
      isFake: currentArticle.isFake,
      source: currentArticle.source,
      topic: currentArticle.topic,
      category: currentArticle.category,
      partisanDirection: currentArticle.partisanDirection,
      sourceTemplateStyle: currentArticle.credibilityLevel,
      endorser: currentArticle.endorser,
      gistCues: currentArticle.gistCues,
      verbatimCues: currentArticle.verbatimCues,
      fttClassification: fttResult,
      surveyData,
      readingTime,
      responseTime,
      timestamp: Date.now(),
      presentationOrder: currentArticle.presentationOrder,
    });

    // Mark article as completed
    const updated = articles.map((a, idx) =>
      idx === currentIndex ? { ...a, completed: true } : a
    );
    setArticles(updated);
    setPendingSurveyData(null);

    // Tested article → surprise/known memory test BEFORE advancing; else advance now.
    if (memoryPlan.current.ids.has(currentArticle.id)) {
      setPhase('memory');
    } else {
      await advanceAfterArticle();
    }
  };

  // ── Persist + advance to next article or debrief ────────────────────
  const advanceAfterArticle = async () => {
    if (currentIndex < articles.length - 1) {
      // Path 2 (LIVE): durable phase save after EVERY article, so a participant
      // who abandons mid-task still has every completed article persisted.
      persistInfoSharing(participantId, articleResponses.current, presentationSequence.current)
        .catch(e => console.error('Live info-sharing save failed (queued for retry):', e));
      setPhase('transition');
    } else {
      tracker.track('taskEnd', {
        taskType: 'info-sharing',
        articlesCompleted: articles.length,
        timestamp: Date.now(),
      });
      try {
        await persistInfoSharing(participantId, articleResponses.current, presentationSequence.current);
      } catch (e) {
        console.error('Durable info-sharing save failed (queued for retry):', e);
      }
      navigate('/info-sharing-debrief');
    }
  };

  // ── Memory test complete → attach to the article's response, then advance ──
  const handleMemoryComplete = async (memoryData: MemoryTestData) => {
    const rec = articleResponses.current.get(currentArticle.id);
    if (rec) rec.memoryTest = memoryData;
    try {
      localStorage.setItem(
        `infoSharingBackup_${participantId}`,
        JSON.stringify({ endTime: Date.now(), responses: Array.from(articleResponses.current.values()) })
      );
    } catch (e) { console.error('Could not write infoSharing backup:', e); }
    tracker.track('memoryTest', { ...memoryData, timestamp: Date.now() });
    await advanceAfterArticle();
  };

  // ── Transition to next article ──────────────────────────────────────

  const handleNextArticle = () => {
    setCurrentIndex(prev => prev + 1);
    setPhase('reading');
    setFttResult(null);
    setArticleStartTime(Date.now());
  };

  // ── Render ──────────────────────────────────────────────────────────

  if (articles.length === 0 || !currentArticle) {
    return <div style={{ textAlign: 'center', padding: '3rem', fontSize: '1rem', color: '#666' }}>Preparing articles...</div>;
  }

  // MEMORY TEST (tested articles only; article not re-shown, no back navigation)
  if (phase === 'memory') {
    return (
      <MemoryTest
        articleId={currentArticle.id}
        role={memoryPlan.current.role[currentArticle.id] || 'incidental'}
        onComplete={handleMemoryComplete}
      />
    );
  }

  // TRANSITION
  if (phase === 'transition') {
    return (
      <div style={{ maxWidth: '550px', margin: '100px auto', textAlign: 'center', padding: '2rem' }}>
        <h2 style={{ fontSize: '1.3rem', fontWeight: 600, marginBottom: '1rem', color: '#1a202c' }}>
          Article {currentIndex + 1} of {articles.length} complete
        </h2>
        <p style={{ color: '#666', marginBottom: '2rem', lineHeight: 1.6 }}>
          You'll now see the next article. Please read and evaluate it the same way.
        </p>
        <button
          onClick={handleNextArticle}
          style={{
            padding: '14px 40px', fontSize: '16px', fontWeight: 600,
            backgroundColor: '#0066cc', color: 'white', border: 'none',
            borderRadius: '8px', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          Continue to Next Article
        </button>
      </div>
    );
  }

  // BEHAVIORAL DECISION (after survey — unanchored)
  if (phase === 'behavioral') {
    return (
      <div style={{ maxWidth: '550px', margin: '60px auto', padding: '2rem' }}>
        <h3 style={{ fontSize: '1.2rem', fontWeight: 600, color: '#1a202c', marginBottom: '1rem', textAlign: 'center' }}>
          One last question about this article
        </h3>
        <div style={{ fontSize: '1rem', color: '#4a5568', marginBottom: '2rem', textAlign: 'center', lineHeight: 1.6 }}>
          If you saw this article in your social media feed, what would you do?
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {[
            { value: 'share' as const, label: 'Share it with others', desc: 'Post, forward, or recommend to friends/colleagues' },
            { value: 'skip' as const, label: 'Scroll past it', desc: 'Move on without engaging' },
            { value: 'flag' as const, label: 'Flag or report it', desc: 'Mark as questionable or report to the platform' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => handleBehavioralDecision(opt.value)}
              disabled={deciding}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                padding: '16px 20px', backgroundColor: '#ffffff',
                borderRadius: '8px', border: '2px solid #e9ecef',
                cursor: deciding ? 'wait' : 'pointer', transition: 'all 0.2s ease',
                textAlign: 'left', opacity: deciding ? 0.6 : 1,
              }}
              onMouseEnter={e => { if (deciding) return; (e.target as HTMLElement).style.borderColor = '#0066cc'; (e.target as HTMLElement).style.backgroundColor = '#f8faff'; }}
              onMouseLeave={e => { (e.target as HTMLElement).style.borderColor = '#e9ecef'; (e.target as HTMLElement).style.backgroundColor = '#ffffff'; }}
            >
              <span style={{ fontSize: '15px', fontWeight: 600, color: '#1a202c' }}>{opt.label}</span>
              <span style={{ fontSize: '13px', color: '#6c757d', marginTop: '4px' }}>{opt.desc}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // SURVEY
  if (phase === 'survey') {
    return <PostArticleSurvey articleId={currentArticle.id} onComplete={handleSurveyComplete} />;
  }

  // READING
  return (
    <div onMouseMove={trackMouseMovement} style={{ maxWidth: '820px', margin: '0 auto', padding: '16px' }}>
      {/* Progress indicator */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '12px', fontSize: '13px', color: '#6c757d',
      }}>
        <span>Article {currentIndex + 1} of {articles.length}</span>
        <span>Read the article below, then continue to the questions</span>
      </div>

      {/* Fake browser chrome — URL bar showing the domain */}
      <div style={{
        backgroundColor: '#f0f0f0',
        borderRadius: '8px 8px 0 0',
        border: '1px solid #ccc',
        borderBottom: 'none',
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        {/* Traffic lights */}
        <div style={{ display: 'flex', gap: '5px' }}>
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#ff5f57' }} />
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#febc2e' }} />
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#28c840' }} />
        </div>
        {/* URL bar */}
        <div style={{
          flex: 1,
          backgroundColor: '#ffffff',
          borderRadius: '4px',
          padding: '5px 12px',
          fontSize: '12px',
          color: '#333',
          fontFamily: 'SF Mono, Monaco, Consolas, monospace',
          border: '1px solid #ddd',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          🔒 {currentArticle.sourceUrl}/{currentArticle.topic}/{
            // Realistic headline slug — NEVER the internal id (a2/c1/d4 would leak
            // the true/fake × partisan cell to an attentive participant).
            currentArticle.headline.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48)
          }
        </div>
      </div>

      {/* Article page replica */}
      <div style={{
        border: '1px solid #ccc',
        borderTop: 'none',
        borderRadius: '0 0 4px 4px',
        overflow: 'hidden',
      }}>
        <NewsArticleRenderer
          article={currentArticle}
          onMouseEnter={() => tracker.track('contentEngagement', {
            element: 'article-page',
            articleId: currentArticle.id,
            source: currentArticle.source,
            startTime: Date.now(),
          })}
          onRegionHover={(region, entering) => {
            const h = hoverRef.current;
            if (entering) {
              if (h.timer) clearTimeout(h.timer);
              h.timer = setTimeout(() => {
                h.active = region;
                trackHover(`${currentArticle.id}:${region}`, 'enter', { articleId: currentArticle.id, region });
              }, 250); // dwell threshold — ignore quick pass-throughs
            } else {
              if (h.timer) { clearTimeout(h.timer); h.timer = null; }
              if (h.active === region) {
                trackHover(`${currentArticle.id}:${region}`, 'leave', { articleId: currentArticle.id, region });
                h.active = null;
              }
            }
          }}
        />
      </div>

      {/* Continue button */}
      <div style={{ textAlign: 'center', marginTop: '24px', marginBottom: '24px' }}>
        <button
          onClick={handleContinueToSurvey}
          style={{
            padding: '14px 48px', fontSize: '16px', fontWeight: 600,
            backgroundColor: '#0066cc', color: 'white', border: 'none',
            borderRadius: '8px', cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease',
          }}
        >
          Continue to Questions →
        </button>
      </div>
    </div>
  );
};

export default InformationSharingTask;
